import apiClient, { apiService } from './apiService';
import {
  Deck,
  DeckVersion,
  DeckWithStats,
  DeckShareLink,
  DeckStats,
  DeckAnalytics,
  CreateShareLinkInput,
  UpdateShareLinkInput,
  UpdateDeckInput,
} from '../types/deck';

const BASE_URL = '/api/decks';

export const deckService = {
  // ============================================
  // STATS
  // ============================================

  /**
   * Get aggregate stats for all user's decks
   */
  async getStats(): Promise<DeckStats> {
    const response = await apiService.get(`${BASE_URL}/stats`);
    return response.data.data;
  },

  // ============================================
  // DECKS
  // ============================================

  /**
   * Get all decks for the current user (with view and link counts)
   */
  async getDecks(): Promise<DeckWithStats[]> {
    const response = await apiService.get(`${BASE_URL}`);
    return response.data.data;
  },

  /**
   * Get a single deck by ID
   */
  async getDeck(id: string): Promise<Deck> {
    const response = await apiService.get(`${BASE_URL}/${id}`);
    return response.data.data;
  },

  /**
   * Upload a new deck
   */
  async uploadDeck(file: File): Promise<Deck> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      console.log('[DeckService] Starting upload:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });

      // Don't set Content-Type header - let browser set it automatically with correct boundary
      const response = await apiService.post(`${BASE_URL}`, formData);
      console.log('[DeckService] Upload successful');
      return response.data.data;
    } catch (error: any) {
      console.error('[DeckService] Upload failed:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        code: error?.code,
      });
      throw error;
    }
  },

  /**
   * Update a deck's metadata
   */
  async updateDeck(id: string, input: UpdateDeckInput): Promise<Deck> {
    const response = await apiService.put(`${BASE_URL}/${id}`, input);
    return response.data.data;
  },

  /**
   * Replace a deck's file with a new version
   */
  async replaceDeck(id: string, file: File): Promise<Deck> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiService.post(`${BASE_URL}/${id}/replace`, formData);
    return response.data.data;
  },

  /**
   * Get version history for a deck
   */
  async getVersionHistory(id: string): Promise<DeckVersion[]> {
    const response = await apiService.get(`${BASE_URL}/${id}/versions`);
    return response.data.data;
  },

  /**
   * Get download URL for a specific version
   */
  async getVersionDownloadUrl(deckId: string, versionId: string): Promise<string> {
    const response = await apiService.get(`${BASE_URL}/${deckId}/versions/${versionId}/download`);
    return response.data.data.url;
  },

  /**
   * Delete a deck
   */
  async deleteDeck(id: string): Promise<void> {
    await apiService.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Get a download URL for a deck
   */
  async getDeckDownloadUrl(id: string): Promise<string> {
    const response = await apiService.get(`${BASE_URL}/${id}/download`);
    return response.data.data.url;
  },

  // ============================================
  // SHARE LINKS
  // ============================================

  /**
   * Get all share links for a deck
   */
  async getShareLinks(deckId: string): Promise<DeckShareLink[]> {
    const response = await apiService.get(`${BASE_URL}/${deckId}/share-links`);
    return response.data.data;
  },

  /**
   * Create a new share link
   */
  async createShareLink(deckId: string, input?: CreateShareLinkInput): Promise<DeckShareLink> {
    const response = await apiService.post(`${BASE_URL}/${deckId}/share-links`, input || {});
    return response.data.data;
  },

  /**
   * Update a share link
   */
  async updateShareLink(deckId: string, linkId: string, input: UpdateShareLinkInput): Promise<DeckShareLink> {
    const response = await apiService.put(`${BASE_URL}/${deckId}/share-links/${linkId}`, input);
    return response.data.data;
  },

  /**
   * Delete a share link
   */
  async deleteShareLink(deckId: string, linkId: string): Promise<void> {
    await apiService.delete(`${BASE_URL}/${deckId}/share-links/${linkId}`);
  },

  // ============================================
  // ANALYTICS
  // ============================================

  /**
   * Get analytics for a deck
   */
  async getAnalytics(deckId: string): Promise<DeckAnalytics> {
    const response = await apiService.get(`${BASE_URL}/${deckId}/analytics`);
    return response.data.data;
  },

  // ============================================
  // PUBLIC VIEWING (No Auth Required)
  // ============================================

  /**
   * Get a deck for public viewing by share token
   */
  async getPublicDeck(token: string): Promise<{
    deck: {
      id: string;
      name: string;
      description: string | null;
      file_type: string;
      file_size: number;
      file_url: string;
    };
    share_link_id: string;
  }> {
    const response = await apiService.get(`/api/public/deck/${token}`);
    return response.data.data;
  },

  /**
   * Record a view for a public deck (returns the created view with its ID)
   */
  async recordPublicView(token: string, input?: {
    viewer_email?: string;
    duration_seconds?: number;
  }): Promise<{ id: string }> {
    const response = await apiService.post(`/api/public/deck/${token}/view`, input || {});
    return response.data.data;
  },

  /**
   * Update the duration of an existing public deck view
   */
  async updatePublicViewDuration(token: string, viewId: string, durationSeconds: number): Promise<void> {
    await apiService.patch(`/api/public/deck/${token}/view/${viewId}`, {
      duration_seconds: durationSeconds,
    });
  },

  /**
   * Get the full URL for updating view duration (for use with sendBeacon)
   */
  getUpdateViewDurationUrl(token: string, viewId: string): string {
    const baseURL = apiClient.defaults.baseURL || '';
    return `${baseURL}/api/public/deck/${token}/view/${viewId}`;
  },

  // ============================================
  // PUBLIC VIEWING — SLUG-BASED (New URL Format)
  // ============================================

  /**
   * Get a deck for public viewing by url_slug
   */
  async getPublicDeckBySlug(linkSlug: string): Promise<{
    deck: {
      id: string;
      name: string;
      description: string | null;
      file_type: string;
      file_size: number;
      file_url: string;
    };
    share_link_id: string;
  }> {
    const response = await apiService.get(`/api/public/deck/slug/${linkSlug}`);
    return response.data.data;
  },

  /**
   * Record a view for a slug-based public deck
   */
  async recordPublicViewBySlug(linkSlug: string, input?: {
    viewer_email?: string;
    duration_seconds?: number;
  }): Promise<{ id: string }> {
    const response = await apiService.post(`/api/public/deck/slug/${linkSlug}/view`, input || {});
    return response.data.data;
  },

  /**
   * Update the duration of an existing slug-based public deck view
   */
  async updatePublicViewDurationBySlug(linkSlug: string, viewId: string, durationSeconds: number): Promise<void> {
    await apiService.patch(`/api/public/deck/slug/${linkSlug}/view/${viewId}`, {
      duration_seconds: durationSeconds,
    });
  },

  /**
   * Get the full URL for updating view duration via sendBeacon (slug-based)
   */
  getUpdateViewDurationUrlBySlug(linkSlug: string, viewId: string): string {
    const baseURL = apiClient.defaults.baseURL || '';
    return `${baseURL}/api/public/deck/slug/${linkSlug}/view/${viewId}`;
  },
};

export default deckService;
